/**
 * Tracing Controls Component
 * Zoom, pan, undo/redo, and other controls for polygon tracing
 */
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  ZoomIn,
  ZoomOut,
  RotateCcw,
  RotateCw,
  X,
  Grid,
  MousePointerClick,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface TracingControlsProps {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomReset: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onClear: () => void;
  canUndo: boolean;
  canRedo: boolean;
  hasPoints: boolean;
  zoom: number;
  showGrid: boolean;
  onToggleGrid: () => void;
  className?: string;
}

export function TracingControls({
  onZoomIn,
  onZoomOut,
  onZoomReset,
  onUndo,
  onRedo,
  onClear,
  canUndo,
  canRedo,
  hasPoints,
  zoom,
  showGrid,
  onToggleGrid,
  className,
}: TracingControlsProps) {
  return (
    <div className={cn('flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-2 sm:gap-2', className)}>
      {/* Zoom Controls */}
      <div className="flex items-center gap-1 border rounded-md p-1 w-full sm:w-auto">
        <Button
          variant="ghost"
          size="sm"
          onClick={onZoomOut}
          className="h-7 w-7 sm:h-7 sm:w-7 p-0"
          title="Zoom Out"
        >
          <ZoomOut className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        </Button>
        <span className="text-xs px-2 min-w-[50px] sm:min-w-[60px] text-center">{Math.round(zoom * 100)}%</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={onZoomIn}
          className="h-7 w-7 sm:h-7 sm:w-7 p-0"
          title="Zoom In"
        >
          <ZoomIn className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onZoomReset}
          className="h-7 px-2 text-xs shrink-0"
          title="Reset Zoom"
        >
          Reset
        </Button>
      </div>

      <Separator orientation="vertical" className="h-6 hidden sm:flex" />

      {/* Undo/Redo */}
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={onUndo}
          disabled={!canUndo}
          className="h-7 w-7 p-0"
          title="Undo (Z)"
        >
          <RotateCcw className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onRedo}
          disabled={!canRedo}
          className="h-7 w-7 p-0"
          title="Redo (Y)"
        >
          <RotateCw className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        </Button>
      </div>

      <Separator orientation="vertical" className="h-6 hidden sm:flex" />

      {/* Clear */}
      <Button
        variant="outline"
        size="sm"
        onClick={onClear}
        disabled={!hasPoints}
        className="h-7 text-xs sm:text-sm"
        title="Clear Current Polygon"
      >
        <X className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
        Clear
      </Button>

      <Separator orientation="vertical" className="h-6 hidden sm:flex" />

      {/* Grid Toggle */}
      <Button
        variant={showGrid ? 'default' : 'outline'}
        size="sm"
        onClick={onToggleGrid}
        className="h-7 w-7 sm:h-7 sm:w-7 p-0"
        title="Toggle Grid"
      >
        <Grid className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
      </Button>

      {/* Instructions */}
      <div className="text-[10px] sm:text-xs text-muted-foreground w-full sm:w-auto sm:ml-auto mt-1 sm:mt-0">
        <MousePointerClick className="h-3 w-3 inline mr-1" />
        <span className="hidden sm:inline">
          {zoom > 1 ? (
            <>Space + drag to pan • Click to add points • Click line to insert • Drag polygon/point to move</>
          ) : (
            <>Click to add points • Click line to insert • Drag polygon/point to move • Double-click point to delete</>
          )}
        </span>
        <span className="sm:hidden">
          {zoom > 1 ? 'Pan: Space+drag • Click to add • Drag to move' : 'Click to add/insert • Drag to move'}
        </span>
      </div>
    </div>
  );
}

